"use client";

import { useMemo } from "react";
import { usePathname } from "next/navigation";
import useStoreKeys from "./useStoreKeys";
import useStorePathname from "./useStorePathname";

export type BreadcrumbLink = {
	text: string;
	href?: string;
};

export interface BreadcrumbProps {
	separator?: string;
	replaceAndWithSymbol?: boolean;
	override?: BreadcrumbLink[];
}

const useBreadcrumbLinks = ({
	separator = "/",
	replaceAndWithSymbol,
	override,
}: BreadcrumbProps) => {
	const { store, lang } = useStoreKeys();
	const pathname = useStorePathname();
	const fullPathname = usePathname();

	const getBreadcrumbLinksFromLocation = () => {
		let href = "";

		// Define the prefix you want to exclude
		const prefixToOmit = `${store}/${lang}`;
		const prefixSegments = prefixToOmit.split("/").filter(Boolean); // ["eu", "en"]

		return fullPathname
			.split("/")
			.filter(Boolean) // Remove empty strings caused by leading or trailing slashes
			.map((value, index, arr) => {
				href = `${href}/${value}`;

				// Skip prefix segments for the text property
				const isPartOfPrefix =
					index < prefixSegments.length && value === prefixSegments[index];
				const text = isPartOfPrefix ? "" : value.replaceAll("-", " ").trim();

				return {
					text,
					href,
				};
			})
			.filter(({ text }) => text !== "");
	};

	const paths = useMemo(() => {
		if (pathname === "/") {
			return undefined;
		}

		let breadcrumbLinks = override || getBreadcrumbLinksFromLocation();

		if (replaceAndWithSymbol) {
			breadcrumbLinks = breadcrumbLinks.map((link) => ({
				text: link.text.replace(/\band\b/g, "&"),
				href: link.href,
			}));
		}

		if (
			!!breadcrumbLinks.length &&
			breadcrumbLinks[0].text &&
			breadcrumbLinks[0]?.text.toLowerCase() !== "home"
		) {
			const sanitizedCrumbs = (breadcrumbLinks = breadcrumbLinks.filter(
				(link) => link.text.toLowerCase() !== "customer care",
			));

			const normalizedStore = store?.replace(/\/$/, "");
			const normalizedLang = lang?.replace(/^\//, "");
			sanitizedCrumbs.unshift({
				text: "home",
				href:
					store !== "global" ? `/${normalizedStore}/${normalizedLang}` : "/",
			});
		}
		// saveBreadcrumbToLocalStorage(breadcrumbLinks);

		return breadcrumbLinks;
	}, [
		pathname,
		override,
		replaceAndWithSymbol,
		getBreadcrumbLinksFromLocation,
	]);

	return {
		paths,
		separator,
	};
};

export default useBreadcrumbLinks;
